import { Link, NavLink, useParams, useSearchParams } from "react-router-dom";
import { Icon, type IconName } from "./icons";
import { MinecraftShell } from "./MinecraftV2";
import { AreaGate } from "./site/SiteShell";
import { Standings } from "./site/Standings";
import { Avatar, EmptyState, Skeleton, formatDateTime, formatHours, formatNumber, humanize, useApi } from "./ui";
import { ErrorState } from "./ui/kit";

type Board = { kind: string; label: string; icon: IconName; detail: string; unit: string; format: (value: unknown) => string };

const boards: Board[] = [
  { kind: "invites", label: "Invites", icon: "invite", detail: "Members who brought friends to SkyBoxy.", unit: "invites", format: formatNumber },
  { kind: "messages", label: "Messages", icon: "message", detail: "The people who keep the chat busy.", unit: "messages", format: formatNumber },
  { kind: "voicetime", label: "Voice time", icon: "mic", detail: "Time spent hanging out in voice channels.", unit: "in voice", format: formatHours },
  { kind: "engagement", label: "Engagement", icon: "spark", detail: "Points for suggestions, polls, and joining events.", unit: "points", format: formatNumber },
];
const boardFor = (kind: string): Board => boards.find((board) => board.kind === kind) || { kind, label: humanize(kind), icon: "trophy", detail: "", unit: "", format: formatNumber };

const ranges = [
  { id: "week", label: "This week", empty: "this week" },
  { id: "month", label: "This month", empty: "this month" },
  { id: "all", label: "All time", empty: "yet" },
] as const;
type RangeId = typeof ranges[number]["id"];

const intro = "The most active people in the SkyBoxy Discord. You can opt out at any time from your member portal.";

export function CommunityLeaderboards() {
  return <AreaGate id="public.community"><MinecraftShell><main id="main">
    <div className="container page-top"><header className="page-head"><h1>Discord leaderboards</h1><p>{intro}</p></header></div>
    <section className="band" aria-label="Leaderboards">
      <div className="container">
        <ul className="board-index">{boards.map((board) => <li key={board.kind}>
          <span className="tile-icon" aria-hidden="true"><Icon name={board.icon} size={20}/></span>
          <h2><Link className="stretched" to={`/discord/leaderboard/${board.kind}`}>{board.label}</Link></h2>
          <p>{board.detail}</p>
          <span className="board-index-go" aria-hidden="true">See the board<Icon name="arrowRight" size={16}/></span>
        </li>)}</ul>
      </div>
    </section>
  </main></MinecraftShell></AreaGate>;
}

export function CommunityLeaderboard() {
  return <AreaGate id="public.community"><MinecraftShell><Leaderboard/></MinecraftShell></AreaGate>;
}

function Leaderboard() {
  const { kind = "messages" } = useParams();
  const [params, setParams] = useSearchParams();
  const range: RangeId = ranges.find((item) => item.id === params.get("range"))?.id || "month";
  const rangeInfo = ranges.find((item) => item.id === range)!;
  const board = boardFor(kind);
  const state = useApi<any>(`/api/v1/public/discord/leaderboards/${encodeURIComponent(kind)}?range=${range}`);
  const items: any[] = Array.isArray(state.data?.items) ? state.data.items : [];
  const search = range === "month" ? "" : `?range=${range}`;
  const setRange = (next: RangeId) => setParams(next === "month" ? {} : { range: next }, { replace: true });
  const code = state.error?.error?.code;
  return <main id="main">
    <div className="container page-top"><header className="page-head"><h1>Discord leaderboards</h1><p>{intro}</p></header></div>
    <section className="band" aria-labelledby="board-title">
      <div className="container">
        <nav className="lb-picker segmented" aria-label="Leaderboard type">{boards.map((item) => <NavLink key={item.kind} to={`/discord/leaderboard/${item.kind}${search}`}><Icon name={item.icon} size={18}/><span>{item.label}</span></NavLink>)}</nav>
        <div className="lb-title">
          <div><h2 id="board-title">{board.label} leaderboard</h2>{board.detail && <p>{board.detail}</p>}</div>
          <div className="segmented" role="group" aria-label="Time range">{ranges.map((item) => <button type="button" key={item.id} aria-pressed={range === item.id} onClick={() => setRange(item.id)}>{item.label}</button>)}</div>
        </div>
        <div aria-busy={state.loading} aria-live="polite">
          {state.loading && !state.data ? <Skeleton rows={5} label="Loading the leaderboard"/>
            : state.error ? code === "page_restricted" || code === "sign_in_required" ? <ErrorState error={state.error}/>
              : <EmptyState icon="alert" title={`${board.label} rankings are not available right now`} description={state.error.error?.message || "The bot feature that tracks this board is not running right now. Try again in a few minutes."} action={<div className="empty-actions"><button type="button" className="button" onClick={state.reload}><Icon name="refresh" size={16}/>Try again</button></div>}/>
            : !items.length ? <EmptyState icon="trophy" title="Nobody has qualified yet" description={`No one has made this board ${rangeInfo.empty}. It fills in as people chat, talk in voice, and invite friends.`}/>
            : <Standings label={`${board.label}, ${rangeInfo.label.toLowerCase()}`} column="Member" valueLabel={board.unit ? humanize(board.unit) : "Score"} unit={board.unit} noun="member"
              items={items.map((item) => ({ key: String(item.userId || item.rank), rank: item.rank, name: item.displayName, picture: <Avatar src={item.avatarUrl} name={item.displayName} size={36}/>, leaderPicture: <Avatar src={item.avatarUrl} name={item.displayName} size={72}/>, value: board.format(item.value) }))}/>}
        </div>
        {state.data?.calculatedAt && <p className="lb-foot">{rangeInfo.label}. Updated {formatDateTime(state.data.calculatedAt)}{state.data.status === "cached" ? " from a saved copy" : ""}. Members can opt out from their portal.</p>}
      </div>
    </section>
  </main>;
}
